import {
  Children,
  type ReactNode,
  isValidElement,
  useEffect,
  useState,
} from 'react';

import classNames from 'classnames';

import styles from './InputMessage.module.css';

import { visuallyHidden } from '../../styles';
import { type PolitenessSetting, StatusMessage } from '../StatusMessage';
import { Text } from '../Text';

export type InputMessageVariants = 'info' | 'invalid' | 'plain' | 'warning';

export interface InputMessageProps {
  /**
   * The input component (e.g. Input, Select, SearchableSelect, StyledSelect,
   * TextArea) to wrap with a message
   */
  children: ReactNode;
  /** CSS class name for the root of the input message */
  className?: string;
  /** The message string to show beneath the input. */
  message?: string;
  /** CSS class name for the message component */
  messageClassName?: string;
  /**
   * The politeness level of the message, for accessbility. Defaults to
   * 'polite'.
   */
  politeness?: PolitenessSetting;
  /**
   * The message variant to use, which sets the background and text color.
   * Defaults to 'plain'.
   */
  variant?: InputMessageVariants;
}

/**
 * Wraps an input component in a pre-styled StatusMessage meant to inform, warn,
 * or invalidate the input. The message is placed below the input; it's always
 * rendered but hidden with CSS. It sets the "politeness", a.k.a aria-live
 * attribute, in order to be accessible.
 *
 * @example
 *
 * ```tsx
 * <InputMessage message="That isn't allowed!" variant="invalid">
 *   <Input />
 * </InputMessage>;
 * ```
 */
export function InputMessage({
  children,
  className,
  message,
  messageClassName,
  politeness = 'polite',
  variant = 'plain',
}: InputMessageProps) {
  const [highlighted, setHighlighted] = useState(false);

  useEffect(() => {
    setHighlighted(
      Children.toArray(children)
        .filter(c => isValidElement<{ highlighted?: boolean }>(c))
        .some(c => c.props.highlighted === true)
    );
  }, [children]);

  const styleClasses = {
    [styles.outline]: highlighted,
    [styles.info]: variant === 'info',
    [styles.invalid]: variant === 'invalid',
    [styles.plain]: variant === 'plain',
    [styles.warning]: variant === 'warning',
  };

  return (
    <div className={classNames(styles.root, className)}>
      {children}
      <div
        className={classNames(styles.spacer, {
          [visuallyHidden]: !message,
          ...styleClasses,
        })}
      ></div>
      <StatusMessage
        politeness={politeness}
        visuallyHidden={!message}
        className={classNames(styles.container, styleClasses, messageClassName)}
      >
        <Text
          variant={
            variant === 'plain' || variant === 'invalid'
              ? 'heading-16'
              : 'heading-12'
          }
        >
          {message}
        </Text>
      </StatusMessage>
    </div>
  );
}
